// frontend/src/App.js - UPDATED with consistent language switcher + admin check
import React, { useState, useEffect } from 'react';
import ResponsiveSidebar from './components/ResponsiveSidebar';
import Dashboard from './components/Dashboard';
import MyRenders from './components/MyRenders';
import UploadRender from './components/UploadRender';
import Settings from './components/Settings';
import LandingPage from './components/LandingPage';
import AdminDashboard from './components/AdminDashboard';
import { api } from './services/api';
import { useNotification } from './components/NotificationContext';
import { useLanguage } from './contexts/LanguageContext';
import { AlertTriangle, Wifi } from 'lucide-react';
import ConnectionTest from './components/ConnectionTest';
import { debugConnection } from './utils/debugConnection';
// ⬆️ at the top of App.js
import { ensureSocket } from "./services/socketService";


// Simple Language Switcher Button Component with flags
const SimpleLanguageButton = () => {
  const { toggleLanguage, isEnglish } = useLanguage();
  
  return (
    <button
      onClick={toggleLanguage}
      className="fixed top-4 right-4 z-50 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 hover:border-gray-500 transition-all flex items-center space-x-2 shadow-lg"
      title={isEnglish ? 'Switch to Chinese (切换到中文)' : 'Switch to English (切换到英文)'}
    >
      <span className="text-lg" role="img" aria-label={isEnglish ? 'US flag' : 'Chinese flag'}>
        {isEnglish ? '🇺🇸' : '🇨🇳'}
      </span>
      <span className="text-sm font-medium">
        {isEnglish ? 'EN' : '中文'}
      </span>
      <span className="text-xs text-gray-400">
        / {isEnglish ? '中文' : 'EN'}
      </span>
    </button>
  );
};

// Login/Register form
const AuthForm = ({ onLogin, onRegister, isLoading, error, onBackToLanding }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showConnectionTest, setShowConnectionTest] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    youtubeChannel: ''
  });
  const { t } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLogin) {
      onLogin({ email: formData.email, password: formData.password });
    } else {
      onRegister(formData);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const isConnectionError = error && (
    error.includes('Failed to fetch') || 
    error.includes('ERR_BLOCKED_BY_CLIENT') ||
    error.includes('Unable to connect')
  );

  return (
    <>
      <SimpleLanguageButton />
      
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="max-w-md w-full space-y-8 p-8">
          {/* Header */}
          <div className="flex justify-start items-start mb-6">
            <button 
              onClick={onBackToLanding}
              className="text-red-500 hover:text-red-400 flex items-center text-sm"
            >
              ← {t('backToHome') || 'Back to home'}
            </button>
          </div>
          
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              {isLogin ? t('signIn') : t('signUp')} {t('toRenderStudio') || 'to Render Studio'}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-400">
              {isLogin ? t('dontHaveAccount') : t('alreadyHaveAccount')}
              <button
                type="button"
                className="text-red-500 hover:text-red-400 font-medium ml-1"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormData({
                    email: '',
                    password: '',
                    firstName: '',
                    lastName: '',
                    youtubeChannel: ''
                  });
                }}
              >
                {isLogin ? t('signUp') : t('signIn')}
              </button>
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded">
                <div className="flex items-start space-x-2">
                  <AlertTriangle size={20} className="text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm">{error}</p>
                    {isConnectionError && (
                      <button
                        type="button"
                        onClick={() => setShowConnectionTest(true)}
                        className="mt-2 inline-flex items-center text-xs text-red-300 hover:text-red-200 underline"
                      >
                        <Wifi size={14} className="mr-1" />
                        {t('testConnection') || 'Test Connection'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="sr-only">{t('firstName')}</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      className="relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
                      placeholder={t('firstName')}
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="sr-only">{t('lastName')}</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      className="relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
                      placeholder={t('lastName')}
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="youtubeChannel" className="sr-only">{t('youtubeChannel')}</label>
                  <input
                    id="youtubeChannel"
                    name="youtubeChannel"
                    type="text"
                    className="relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
                    placeholder={`${t('youtubeChannel')} (${t('optional') || 'Optional'})`}
                    value={formData.youtubeChannel}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
            
            <div>
              <label htmlFor="email" className="sr-only">{t('email')}</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
                placeholder={t('email')}
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">{t('password')}</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                minLength="6"
                className="relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
                placeholder={`${t('password')} (${t('min6chars') || 'min 6 characters'})`}
                value={formData.password}
                onChange={handleChange}
              />
            </div>
            
            {!isLogin && (
              <div className="text-sm text-gray-400">
                <p>{t('byCreatingAccount') || 'By creating an account, you get'}:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>{t('3FreeRenders') || '3 free renders per month'}</li>
                  <li>{t('1080pResolution') || '1080p resolution'}</li>
                  <li>{t('standardQueue') || 'Standard queue priority'}</li>
                  <li>{t('accessToUpgrade') || 'Access to upgrade plans'}</li>
                </ul>
              </div>
            )}
            
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isLogin ? (t('signingIn') || 'Signing in...') : (t('creatingAccount') || 'Creating account...')}
                  </span>
                ) : (
                  isLogin ? t('signIn') : t('createAccount') || 'Create account'
                )}
              </button>
            </div>
          </form>

          {isConnectionError && (
            <div className="text-center">
              <button
                onClick={() => setShowConnectionTest(true)}
                className="text-blue-400 hover:text-blue-300 text-sm underline"
              >
                {t('runConnectionTest') || 'Run Connection Diagnostics'}
              </button>
            </div>
          )}
        </div>
      </div>

      <ConnectionTest 
        show={showConnectionTest} 
        onClose={() => setShowConnectionTest(false)} 
      />
    </>
  );
};

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [renderQueue, setRenderQueue] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showLanding, setShowLanding] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const { addToast } = useNotification();
  
  // Authentication check
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsLoading(false);
        setIsAuthenticated(false);
        setShowLanding(true);
        return;
      }

      try {
        const response = await api.getCurrentUser();
        if (response.success && response.data) {
          setUser(response.data);
          setIsAuthenticated(true);
          setShowLanding(false);
        } else {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          setShowLanding(true);
        }
      } catch (error) {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setShowLanding(true);
        if (!error.message.includes('Authentication required')) {
          addToast(`Authentication error: ${error.message}`, 'error');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [addToast]);
  
  // Fetch renders
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const fetchRenders = async () => {
      try {
        const response = await api.getRenderQueue();
        if (response.success && response.data) {
          setRenderQueue(response.data);
        } else {
          setRenderQueue([]);
        }
      } catch (error) {
        if (error.message.includes('Authentication required')) {
          setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem('token');
          setShowLanding(true);
          addToast('Session expired. Please login again.', 'error');
        } else {
          addToast(`Error loading renders: ${error.message}`, 'error');
          setRenderQueue([]);
        }
      }
    };
    
    fetchRenders();
    const interval = setInterval(fetchRenders, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user, addToast]);

  // Login
  const handleLogin = async (credentials) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const response = await api.login(credentials);
      if (response.success) {
        const userResponse = await api.getCurrentUser();
        if (userResponse.success && userResponse.data) {
          setUser(userResponse.data);
          // ✅ Join personal room after login
const socket = await ensureSocket();
socket.emit("joinUserRoom", userResponse.data.id);

          setIsAuthenticated(true);
          setShowLanding(false);
          setShowAuth(false);
          addToast(`Welcome back, ${userResponse.data.firstName}!`, 'success');
        }
      } else {
        setAuthError('Login failed. Please check your credentials.');
      }
    } catch (error) {
      setAuthError(error.message || 'Login failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  // Register
  const handleRegister = async (registrationData) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const response = await api.register(registrationData);
      if (response.success) {
        const userResponse = await api.getCurrentUser();
        if (userResponse.success && userResponse.data) {
          setUser(userResponse.data);
          // ✅ Join personal room after login
const socket = await ensureSocket();
socket.emit("joinUserRoom", userResponse.data.id);

          setIsAuthenticated(true);
          setShowLanding(false);
          setShowAuth(false);
          addToast(`Welcome to Render Studio, ${userResponse.data.firstName}!`, 'success');
        }
      } else {
        setAuthError('Registration failed. Please try again.');
      }
    } catch (error) {
      if (error.message.includes('already exists')) {
        setAuthError('An account with this email already exists. Please sign in instead.');
      } else if (error.message.includes('validation')) {
        setAuthError('Please check all required fields and try again.');
      } else {
        setAuthError(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setAuthLoading(false);
    }
  };
  
  // Logout
  const handleLogout = async () => {
    try {
      await api.logout();
      setUser(null);
      setIsAuthenticated(false);
      setRenderQueue([]);
      setShowLanding(true);
      setShowAuth(false);
      localStorage.removeItem('token');
      addToast('Logged out successfully', 'success');
    } catch {
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
      setRenderQueue([]);
      setShowLanding(true);
      setShowAuth(false);
    }
  };

  const handleGetStarted = () => {
    setShowLanding(false);
    setShowAuth(true);
  };

  const handleBackToLanding = () => {
    setShowAuth(false);
    setShowLanding(true);
    setAuthError('');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600 mx-auto"></div>
          <p className="text-white mt-4">Loading Render Studio...</p>
        </div>
      </div>
    );
  }

  // Landing
  if (showLanding && !isAuthenticated) {
    return <LandingPage onGetStarted={handleGetStarted} />;
  }

  // Auth
  if (showAuth && !isAuthenticated) {
    return (
      <AuthForm 
        onLogin={handleLogin}
        onRegister={handleRegister}
        isLoading={authLoading}
        error={authError}
        onBackToLanding={handleBackToLanding}
      />
    );
  }

  // Main app
  if (isAuthenticated && user) {
    // ✅ Admin check
    if (user.role === 'admin' || user.isAdmin) {
      return <AdminDashboard user={user} />;
    }

    return (
      <div className="flex h-screen bg-gray-900 text-white overflow-hidden">
        <SimpleLanguageButton />
        <ResponsiveSidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          user={user}
          onLogout={handleLogout}
        />
        <div className="flex-1 overflow-y-auto">
  {activeTab === 'dashboard' && <Dashboard renderQueue={renderQueue} isLoading={false} user={user} setActiveTab={setActiveTab} />}
  {activeTab === 'renders' && <MyRenders renderQueue={renderQueue} isLoading={false} setActiveTab={setActiveTab} />}
  {activeTab === 'upload' && <UploadRender setActiveTab={setActiveTab} />}
  {activeTab === 'settings' && <Settings user={user} />}
</div>
      </div>
    );
  }

  return null;
}

// Debug diagnostics
export const runDiagnostics = () => {
  debugConnection().then(results => {
    console.log('📋 Full diagnostic report:', results);
    const issues = results.tests.filter(test => test.status === 'failed');
    if (issues.length > 0) {
      console.log('⚠️ Issues found:');
      issues.forEach(issue => console.log(`❌ ${issue.test}: ${issue.error}`));
      if (issues.some(i => i.error.includes('ERR_BLOCKED_BY_CLIENT'))) {
        console.log('🛠️ Solution: Disable ad blocker for localhost');
      }
      if (issues.some(i => i.error.includes('Failed to fetch'))) {
        console.log('🛠️ Solution: Check if backend server is running on port 5001');
      }
      if (issues.some(i => i.error.includes('CORS'))) {
        console.log('🛠️ Solution: Check CORS configuration in backend');
      }
    } else {
      console.log('✅ All connection tests passed');
    }
  });
};

window.debugRenderStudio = runDiagnostics;

export default App;
